/**
 * Pulse — org-wide engagement dashboard.
 *
 * HR_ADMIN+. Shows the latest engagement score, eNPS, response
 * rate, a 6-month trend line, and a per-department breakdown.
 *
 * The trend chart and department breakdown only populate after
 * enough responses exist; the empty-state copy hints at that
 * rather than rendering a misleading flat-zero chart.
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { getDashboard, type PulseDashboard } from "@/api/pulse";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { PageHeader } from "@/components/layout/PageHeader";
import { pageTransition } from "@/lib/utils";

export function PulseDashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<PulseDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getDashboard()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        if (!cancelled) setError("Could not load engagement dashboard.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <Card className="border-danger/40 bg-danger/5">
        <p className="text-sm text-danger">{error}</p>
      </Card>
    );
  }
  if (!data) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-32 rounded bg-border/30 animate-pulse" />
        ))}
      </div>
    );
  }

  const responseRatePct = Math.round(data.response_rate * 100);
  const trendData = data.trend.map((t) => ({
    label: t.period_label.slice(5), // "MM" only — drop year for axis density
    score: t.engagement_score,
    responses: t.response_count,
  }));

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Pulse Dashboard"
        subtitle="How your team is feeling — at a glance."
        actions={
          <Button variant="ghost" size="sm" onClick={() => navigate("/pulse")}>
            <ArrowLeft className="h-3.5 w-3.5" />
            Surveys
          </Button>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <MetricCard
          label="Engagement"
          value={
            data.engagement_score !== null
              ? `${data.engagement_score.toFixed(0)}/100`
              : "—"
          }
          hint="Last 30 days, 1–5 questions"
        />
        <MetricCard
          label="eNPS"
          value={
            data.enps
              ? `${data.enps.score >= 0 ? "+" : ""}${data.enps.score.toFixed(0)}`
              : "—"
          }
          hint={
            data.enps
              ? `${data.enps.total} response${data.enps.total === 1 ? "" : "s"}`
              : "No eNPS responses yet"
          }
        />
        <MetricCard
          label="Response rate"
          value={`${responseRatePct}%`}
          hint={`${data.total_responses_30d} responses in last 30 days`}
        />
      </div>

      {/* Trend */}
      <Card className="mb-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-text-muted mb-2">
          Engagement trend (6 months)
        </h3>
        {trendData.length > 0 ? (
          <TrendSparkline data={trendData} />
        ) : (
          <p className="text-sm text-text-muted">
            Trend data appears after a few survey cycles. Send your first
            survey to start the line.
          </p>
        )}
      </Card>

      {/* Department breakdown */}
      <Card>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-text-muted mb-2">
          Engagement by department (last 90 days)
        </h3>
        {data.department_breakdown.length === 0 ? (
          <p className="text-sm text-text-muted">
            Department breakdown shows up once non-anonymous surveys have
            been answered. Anonymous surveys won't appear here by design.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {data.department_breakdown.map((d) => (
              <li
                key={d.department_name + (d.department_id ?? "none")}
                className="flex items-center justify-between py-2.5"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-primary truncate">
                    {d.department_name}
                  </p>
                  <p className="text-xs text-text-muted">
                    {d.response_count} response{d.response_count === 1 ? "" : "s"}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <ScoreBar score={d.engagement_score} />
                  <Badge variant="info" className="min-w-[3.5rem] text-center">
                    {d.engagement_score !== null
                      ? d.engagement_score.toFixed(0)
                      : "—"}
                  </Badge>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </motion.div>
  );
}


function MetricCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <Card>
      <p className="text-xs uppercase tracking-wide text-text-muted">{label}</p>
      <p className="text-2xl font-semibold font-heading text-text-primary mt-1">
        {value}
      </p>
      <p className="text-xs text-text-muted mt-1">{hint}</p>
    </Card>
  );
}


/**
 * SVG sparkline for the engagement trend. Hand-rolled rather than
 * pulling recharts in — six data points doesn't justify a charting
 * library, and this stays consistent with the rest of the small
 * inline data-viz on the dashboard.
 */
function TrendSparkline({
  data,
}: {
  data: { label: string; score: number | null; responses: number }[];
}) {
  // Pad so the line touches the viewport edges with a little
  // breathing room. Heights map 0..100 → 8..72 (taller = better).
  const W = 600;
  const H = 200;
  const padX = 24;
  const padY = 16;
  const innerW = W - padX * 2;
  const innerH = H - padY * 2;

  const points = data.map((d, i) => {
    const x = padX + (i / Math.max(1, data.length - 1)) * innerW;
    const y =
      d.score === null ? null : padY + (1 - d.score / 100) * innerH;
    return { x, y, label: d.label, score: d.score };
  });

  // Build a polyline connecting only points with a real score —
  // gaps are skipped (rather than drawn as zero, which would lie
  // about the data). 0-score periods do still render a marker.
  let path = "";
  let started = false;
  for (const p of points) {
    if (p.y === null) continue;
    path += (started ? " L " : "M ") + `${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
    started = true;
  }

  return (
    <div className="space-y-2">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-48"
        role="img"
        aria-label="Engagement score over the last 6 months"
      >
        {/* y-axis gridlines at 25/50/75 */}
        {[25, 50, 75].map((g) => {
          const y = padY + (1 - g / 100) * innerH;
          return (
            <line
              key={g}
              x1={padX}
              x2={W - padX}
              y1={y}
              y2={y}
              stroke="rgb(var(--color-border))"
              strokeDasharray="3 3"
            />
          );
        })}
        {path && (
          <path
            d={path}
            fill="none"
            stroke="rgb(var(--color-accent))"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
        {points.map((p, i) =>
          p.y === null ? null : (
            <circle
              key={i}
              cx={p.x}
              cy={p.y}
              r={3.5}
              fill="rgb(var(--color-accent))"
            >
              <title>
                {p.label}: {p.score?.toFixed(0)}
              </title>
            </circle>
          ),
        )}
      </svg>
      <div className="flex justify-between px-2 text-[11px] text-text-muted">
        {points.map((p, i) => (
          <span key={i}>{p.label}</span>
        ))}
      </div>
    </div>
  );
}


function ScoreBar({ score }: { score: number | null }) {
  const w = score !== null ? Math.max(2, Math.min(100, score)) : 0;
  return (
    <div className="hidden sm:block w-32 h-1.5 rounded-full bg-border/60 overflow-hidden">
      <div
        className="h-1.5 rounded-full bg-accent transition-all"
        style={{ width: `${w}%` }}
      />
    </div>
  );
}
